import { Lock, LogOut, Pencil } from "lucide-react"

import { Avatar } from "@/components/tuw/Avatar"
import { Button } from "@/components/ui/button"
import { signOut } from "@/lib/orderActions"

// "Member since Mar 2024" — month + year only (the info card carries the full
// dates; this is the glanceable chip).
function monthYear(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" })
}

// The Profile identity header: the customer's own avatar (with the Insider lock
// decorator, gated on identity.insider — seam #1, never a freelancer's), name +
// @handle, a chip row, and the Edit / Sign out actions. The badge overlay + the
// sign-out call mirror NavAccountBlock (D5/D8 reuse). Sign-out routes through the
// shared hard-navigation signOut helper — a plain Inertia router.delete never
// leaves the page (see the helper's note; the browser must full-page navigate to
// /users/sign_in).
export default function ProfileHeader({ identity, onEdit }) {
  const since = monthYear(identity?.memberSince)

  // P8-H7 "Muster" — `.pf-id` wraps at ≤640 (design/spec/account/profile.html
  // `@media (max-width:640px)`). `sm:` is `width >= 640`, so at EXACTLY 640 the
  // header was still a row; `min-[641px]` compiles to `@media (width >= 641px)`,
  // which is the first row width and leaves 640 stacked as ruled. Same literal on
  // the action cluster's `self-center` below.
  return (
    <div
      data-testid="profile-identity"
      className="flex flex-col gap-5 rounded-2xl border border-border bg-card px-[26px] py-[22px] min-[641px]:flex-row min-[641px]:items-center"
    >
      <span className="relative shrink-0 self-start">
        <Avatar
          initials={identity?.initials ?? "?"}
          name={identity?.name}
          src={identity?.avatarUrl}
          size={76}
          me
        />
        {identity?.insider && (
          <span
            title="Insider"
            aria-label="Insider"
            className="absolute -right-1 -bottom-1 grid size-[26px] place-items-center rounded-full border-[3px] border-card bg-primary text-primary-foreground"
          >
            <Lock className="size-[12px]" strokeWidth={2.5} />
          </span>
        )}
      </span>

      <div className="min-w-0 flex-1">
        <h1 className="font-display text-2xl font-bold tracking-[-0.4px] text-foreground">
          {identity?.name}
        </h1>
        {identity?.username && (
          <p className="mt-0.5 text-[13.5px] text-ink-3">@{identity.username}</p>
        )}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {identity?.insider && (
            <span className="inline-flex items-center gap-1 rounded-md bg-[var(--red-soft)] px-2 py-0.5 text-xs font-semibold text-primary">
              <Lock className="size-3" strokeWidth={2.4} />
              Insider member
            </span>
          )}
          {since && (
            <span className="text-xs font-medium text-ink-3">Member since {since}</span>
          )}
        </div>
      </div>

      <div
        data-testid="profile-identity-actions"
        className="flex shrink-0 items-center gap-2.5 self-start min-[641px]:self-center"
      >
        <Button onClick={onEdit}>
          <Pencil className="size-4" strokeWidth={2} />
          Edit profile
        </Button>
        <Button variant="outline" onClick={signOut}>
          <LogOut className="size-4" strokeWidth={2} />
          Sign out
        </Button>
      </div>
    </div>
  )
}
